// src/components/annotation/hooks/useImageAnnotation.ts

import { useState, useRef, useCallback, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  addBoundingBox,
  addRejectedBoundingBoxByIndex,
  selectAllBoundingBoxes,
  selectedBoundingBox as selectSelectedBoundingBox,
  setAcceptedBoundingBoxes,
  setRejectedBoundingBoxes,
  setSelectedBoundingBox,
  updateBoundingBox,
  removeBoundingBox,
  addEditingTagId,
  undo as undoAction,
  redo as redoAction,
  saveStateToHistory,
  resetAllBoundingBoxes,
  removeEditingTagId,
  markBoundingBoxAsEdited,
} from '../../../store/slices/annotationSlice'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import { migrateBoundingBoxToPoints, isAxisAligned, Point } from '../../../utils/helpers'
import { bboxStrategy } from '../strategies/BBoxStrategy'
import { obbStrategy } from '../strategies/OBBStrategy'
import type {
  GeometryStrategy,
  InteractionContext,
  ResizeHandleName,
  KeyModifiers,
} from '../strategies/GeometryStrategy'
import { Tag } from '../../../types/models/tag'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { getRotatedResizeCursor } from '../utils/cursor'
import { calculateZoomForBBox } from '../utils/drawing'

export interface MousePosition {
  x: number
  y: number
}

export interface PopupState {
  show: boolean
  position: MousePosition
  box: AnnotationDataBoundingBox | null
}

export const useImageAnnotation = (
  useBBoxes: boolean,
  isEditable = true,
  visibleBBoxes?: AnnotationDataBoundingBox[],
  contentWidth?: number,
  contentHeight?: number
) => {
  // Coordinate space converters: boxes are stored in content space, displayed in natural space
  const contentToNatural = useCallback(
    (box: AnnotationDataBoundingBox): AnnotationDataBoundingBox => {
      if (!imageRef.current || !contentWidth || !contentHeight) return box
      const natW = imageRef.current.naturalWidth
      const natH = imageRef.current.naturalHeight
      if (contentWidth === natW && contentHeight === natH) return box // No conversion needed

      const scaleX = natW / contentWidth
      const scaleY = natH / contentHeight

      if (box.points && box.points.length === 8) {
        return {
          ...box,
          points: box.points.map((coord, idx) => (idx % 2 === 0 ? coord * scaleX : coord * scaleY)),
          xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
          ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
          xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
          ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
        }
      }
      return {
        ...box,
        xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
        ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
        xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
        ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
      }
    },
    [contentWidth, contentHeight]
  )

  const naturalToContent = useCallback(
    (box: AnnotationDataBoundingBox): AnnotationDataBoundingBox => {
      if (!imageRef.current || !contentWidth || !contentHeight) return box
      const natW = imageRef.current.naturalWidth
      const natH = imageRef.current.naturalHeight
      if (contentWidth === natW && contentHeight === natH) return box // No conversion needed

      const scaleX = contentWidth / natW
      const scaleY = contentHeight / natH

      if (box.points && box.points.length === 8) {
        return {
          ...box,
          points: box.points.map((coord, idx) => (idx % 2 === 0 ? coord * scaleX : coord * scaleY)),
          xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
          ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
          xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
          ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
        }
      }
      return {
        ...box,
        xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
        ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
        xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
        ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
      }
    },
    [contentWidth, contentHeight]
  )

  // Used to reset the state
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawingStartPoint, setDrawingStartPoint] = useState<Point | null>(null)
  const [newBoundingBox, setNewBoundingBox] = useState<AnnotationDataBoundingBox | null>(null)
  const selectedBBox = useAppSelector(selectSelectedBoundingBox)
  const selectedBBoxRef = useRef<AnnotationDataBoundingBox | null>(null)
  const [resizingHandle, setResizingHandle] = useState<string | null>(null)
  // Store interaction metadata for drag operations (e.g., rotation)
  const interactionRef = useRef<{
    pivot?: { x: number; y: number }
    startPoints?: number[]
    // OBB creation workflow
    axisStart?: Point
    axisEnd?: Point
    halfWidthPxImage?: number
    // For undo gating: snapshot of original points at mousedown
    originalPoints?: number[]
  } | null>(null)
  const [panOffset, setPanOffset] = useState<MousePosition>({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  // const [hoveredBox, setHoveredBox] = useState<AnnotationDataBoundingBox | null>(null)

  const imageRef = useRef<HTMLImageElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const lastPanPointRef = useRef<MousePosition>({ x: 0, y: 0 })
  const suppressTagPopupRef = useRef<boolean>(false)

  // Keep a ref in sync with selectedBBox to avoid stale closures during drag
  useEffect(() => {
    selectedBBoxRef.current = selectedBBox || null
  }, [selectedBBox])

  const [mousePosition, setMousePosition] = useState<MousePosition>({
    x: 0,
    y: 0,
  })
  const [contrast, setContrast] = useState(0)
  const allBoundingBoxes = useAppSelector(selectAllBoundingBoxes)
  const allBoxesLength = allBoundingBoxes?.length ?? 0

  const [popupState, setPopupState] = useState<PopupState>({
    show: false,
    position: { x: 0, y: 0 },
    box: null,
  })

  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const isClassificationProject = project?.annotation_type === 'classification'
  const isOBBProject = project?.annotation_type === 'oriented_bounding_box'

  // Creation state machine for OBB-only projects
  const [creationPhase, setCreationPhase] = useState<'idle' | 'axisStart' | 'axisFixed'>('idle')
  const [creationAxisStart, setCreationAxisStart] = useState<Point | null>(null)

  // PERFORMANCE: Skip Redux updates entirely during resize to eliminate snippet re-renders
  // The canvas still updates in real-time via selectedBBox, but snippets don't update until mouse up
  // This is fine because you're watching the main canvas during drag, not the snippet list
  const skipReduxDuringDragRef = useRef<boolean>(true) // Set to false to re-enable throttled updates

  // Build OBB points from axis (p1->p2) and half-width in image pixels
  const buildObbPoints = useCallback((p1: Point, p2: Point, halfWidthPxImage: number): number[] => {
    const ux = p2.x - p1.x
    const uy = p2.y - p1.y
    const len = Math.hypot(ux, uy) || 1
    const uux = ux / len
    const uuy = uy / len
    const vx = -uuy
    const vy = uux
    const cx = (p1.x + p2.x) / 2
    const cy = (p1.y + p2.y) / 2
    const halfLen = len / 2
    const tlx = cx - uux * halfLen - vx * halfWidthPxImage
    const tly = cy - uuy * halfLen - vy * halfWidthPxImage
    const trx = cx + uux * halfLen - vx * halfWidthPxImage
    const tryy = cy + uuy * halfLen - vy * halfWidthPxImage
    const brx = cx + uux * halfLen + vx * halfWidthPxImage
    const bry = cy + uuy * halfLen + vy * halfWidthPxImage
    const blx = cx - uux * halfLen + vx * halfWidthPxImage
    const bly = cy - uuy * halfLen + vy * halfWidthPxImage
    return [tlx, tly, trx, tryy, brx, bry, blx, bly]
  }, [])

  const editingTagIds = useAppSelector((state) => state.annotation.editingTagIds)

  const getGeometryStrategy = useCallback(
    (box: AnnotationDataBoundingBox | null | undefined): GeometryStrategy => {
      if (isOBBProject) return obbStrategy
      if (!box) return bboxStrategy
      const points = box.points
      if (points && points.length === 8 && !isAxisAligned(points)) {
        return obbStrategy
      }
      return bboxStrategy
    },
    [isOBBProject]
  )

  // Compute a CSS cursor that approximates the intended resize direction for OBB
  const getCursorForHandle = useCallback(
    (box: AnnotationDataBoundingBox, handle: string): string => {
      const pts: number[] =
        box.points && box.points.length === 8
          ? box.points
          : [
              box.xmin ?? 0,
              box.ymin ?? 0,
              box.xmax ?? 0,
              box.ymin ?? 0,
              box.xmax ?? 0,
              box.ymax ?? 0,
              box.xmin ?? 0,
              box.ymax ?? 0,
            ]
      const tl = { x: pts[0], y: pts[1] }
      const tr = { x: pts[2], y: pts[3] }
      const br = { x: pts[4], y: pts[5] }
      const bl = { x: pts[6], y: pts[7] }
      const u = { x: tr.x - tl.x, y: tr.y - tl.y } // top edge direction
      const v = { x: bl.x - tl.x, y: bl.y - tl.y } // left edge direction
      const uAngle = Math.atan2(u.y, u.x)
      const vAngle = Math.atan2(v.y, v.x)

      const pickCursor = (angleRad: number): string => {
        const custom = getRotatedResizeCursor(angleRad)
        if (custom) return custom.css
        // Fallback to nearest system cursors if custom cannot be generated
        const deg = ((angleRad * 180) / Math.PI + 360) % 180
        const candidates: Array<{ deg: number; cursor: string }> = [
          { deg: 0, cursor: 'ew-resize' },
          { deg: 45, cursor: 'nwse-resize' },
          { deg: 90, cursor: 'ns-resize' },
          { deg: 135, cursor: 'nesw-resize' },
        ]
        let best = candidates[0]
        let bestDiff = 999
        for (const c of candidates) {
          const diff = Math.min(Math.abs(deg - c.deg), Math.abs(180 - Math.abs(deg - c.deg)))
          if (diff < bestDiff) {
            bestDiff = diff
            best = c
          }
        }
        return best.cursor
      }

      switch (handle) {
        case 'top':
        case 'bottom':
          // Cursor perpendicular to the edge: use normal to top edge
          return pickCursor(uAngle + Math.PI / 2)
        case 'left':
        case 'right':
          // Cursor perpendicular to the edge: use normal to left edge
          return pickCursor(vAngle + Math.PI / 2)
        case 'topLeft': {
          // Diagonal axis through opposite corner (tl -> br)
          const angle = Math.atan2(br.y - tl.y, br.x - tl.x)
          return pickCursor(angle)
        }
        case 'bottomRight': {
          // Diagonal axis through opposite corner (br -> tl)
          const angle = Math.atan2(tl.y - br.y, tl.x - br.x)
          return pickCursor(angle)
        }
        case 'topRight': {
          // Diagonal axis through opposite corner (tr -> bl)
          const angle = Math.atan2(bl.y - tr.y, bl.x - tr.x)
          return pickCursor(angle)
        }
        case 'bottomLeft': {
          // Diagonal axis through opposite corner (bl -> tr)
          const angle = Math.atan2(tr.y - bl.y, tr.x - bl.x)
          return pickCursor(angle)
        }
        default:
          return 'default'
      }
    },
    []
  )

  const getMousePos = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): MousePosition => {
      if (!canvasRef.current || !imageRef.current) return { x: 0, y: 0 }

      const rect = canvasRef.current.getBoundingClientRect()
      const scaleX = canvasRef.current.width / rect.width
      const scaleY = canvasRef.current.height / rect.height

      const canvasX = (e.clientX - rect.left) * scaleX
      const canvasY = (e.clientY - rect.top) * scaleY

      // Undo zoom/pan to get world coordinates in pre-transform canvas space
      const centerX = canvasRef.current.width / 2
      const centerY = canvasRef.current.height / 2
      const worldX = (canvasX - centerX) / zoomLevel + centerX - panOffset.x
      const worldY = (canvasY - centerY) / zoomLevel + centerY - panOffset.y

      // Map world canvas coords into image coords using contain-fit mapping
      // CRITICAL FIX: Always use natural dimensions (displayed image) instead of content metadata
      // The bounding box coordinates are relative to the displayed image, not original metadata
      const imgW = imageRef.current.naturalWidth || 1
      const imgH = imageRef.current.naturalHeight || 1
      const fitScale = Math.min(canvasRef.current.width / imgW, canvasRef.current.height / imgH)
      const drawW = imgW * fitScale
      const drawH = imgH * fitScale
      const offsetX = (canvasRef.current.width - drawW) / 2
      const offsetY = (canvasRef.current.height - drawH) / 2

      let x = (worldX - offsetX) / fitScale
      let y = (worldY - offsetY) / fitScale

      // Clamp to image bounds (use natural dimensions)
      x = Math.max(0, Math.min(imageRef.current.naturalWidth, x))
      y = Math.max(0, Math.min(imageRef.current.naturalHeight, y))

      // Only update mouse position state if we're in a bounding box project
      if (useBBoxes) {
        setMousePosition({ x, y })
      }

      return { x, y }
    },
    [zoomLevel, panOffset, useBBoxes]
  )

  useEffect(() => {
    setPopupState({
      show: false,
      position: { x: 0, y: 0 },
      box: null,
    })
    const ctx = canvasRef?.current?.getContext('2d')
    if (ctx && imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, ctx.canvas.width, ctx.canvas.height)
    }
  }, [canvasRef, imageRef])

  const recenterImage = useCallback(() => {
    setZoomLevel(0.85)
    setPanOffset({ x: 0, y: 0 })
    setContrast(0)
    dispatch(setSelectedBoundingBox(undefined))
    setIsDrawing(false)
    setNewBoundingBox(null)
    // Ensure OBB creation workflow is reset when navigating to a new image
    setCreationPhase('idle')
    setCreationAxisStart(null)
    setPopupState({
      show: false,
      position: { x: 0, y: 0 },
      box: null,
    })
  }, [dispatch])

  const undo = useCallback(() => {
    dispatch(undoAction())
  }, [dispatch])

  const redo = useCallback(() => {
    dispatch(redoAction())
  }, [dispatch])

  const reset = useCallback(() => {
    dispatch(saveStateToHistory()) // Save current state before resetting
    dispatch(setRejectedBoundingBoxes([]))
    dispatch(setAcceptedBoundingBoxes([]))
    dispatch(resetAllBoundingBoxes())
  }, [dispatch])

  const removeAll = useCallback(() => {
    dispatch(saveStateToHistory()) // Save current state before removing all
    dispatch(setRejectedBoundingBoxes(allBoundingBoxes.map((b) => b.class_index)))
  }, [allBoundingBoxes, dispatch])

  const removeAllTags = useCallback(
    (tagId: string) => {
      dispatch(saveStateToHistory())
      // Remove all boxes with the given tag name
      const boxesToRemove = visibleBBoxes?.filter((box) => box.name === tagId)
      boxesToRemove?.forEach((box) => {
        dispatch(addRejectedBoundingBoxByIndex(box.class_index))
      })
      // Remove the tag ID from editing tags
      dispatch(removeEditingTagId(tagId))
    },
    [dispatch, visibleBBoxes]
  )

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isEditable) return
      e.preventDefault()
      e.stopPropagation()
      const { x, y } = getMousePos(e)
      const clickedBox = visibleBBoxes?.find((box) => {
        // Convert box from content space to natural space for hit testing
        const boxNatural = contentToNatural(box)
        return (
          x >= (boxNatural.xmin ?? 0) &&
          x <= (boxNatural.xmax ?? 0) &&
          y >= (boxNatural.ymin ?? 0) &&
          y <= (boxNatural.ymax ?? 0)
        )
      })

      if (clickedBox && canvasRef?.current) {
        const rect = canvasRef.current.getBoundingClientRect()

        let tagX = e.clientX - rect.left
        let tagY = e.clientY - rect.top

        // Adjust position to ensure tag selector is visible
        if (tagX + 250 > rect.width) {
          tagX = tagX - 250
        }

        // Check if mouse is in the bottom half of the viewport
        const isBottomHalf = e.clientY > window.innerHeight / 2

        // If in bottom half, show above mouse, otherwise show below
        if (isBottomHalf) {
          tagY = tagY - 20 // Show above mouse
        } else {
          tagY = tagY + 20 // Show below mouse
        }

        setPopupState({ show: true, position: { x: tagX, y: tagY }, box: null })
      }
    },
    [visibleBBoxes, getMousePos, isEditable, contentToNatural]
  )

  const handleTagChange = useCallback(
    (newTag: Tag) => {
      if (popupState.box) {
        const updatedBBox = { ...popupState.box, name: newTag.id }
        dispatch(updateBoundingBox({ updatedBBox }))
        setPopupState((prev) => ({ ...prev, show: false, box: null }))
      }
    },
    [popupState.box, dispatch]
  )

  const removeBox = useCallback(
    (box: AnnotationDataBoundingBox) => {
      const classIndex =
        box.class_index !== undefined && box.class_index !== -1
          ? box.class_index
          : allBoundingBoxes.find((b) =>
              b.points && box.points && b.points.length === 8 && box.points.length === 8
                ? b.points.every((v, i) => v === (box.points as number[])[i])
                : b.xmin === box.xmin &&
                  b.ymin === box.ymin &&
                  b.xmax === box.xmax &&
                  b.ymax === box.ymax
            )?.class_index ?? -1

      if (classIndex === -1) return

      dispatch(saveStateToHistory())
      // Actually remove the box from allBoundingBoxes instead of just marking it as rejected
      dispatch(removeBoundingBox({ classIndex }))
      if (box.name) {
        dispatch(removeEditingTagId(box.name))
      }
    },
    [dispatch, allBoundingBoxes]
  )

  const handleBoxDelete = useCallback(
    (box: AnnotationDataBoundingBox | null) => {
      if (box) {
        if (popupState.box) {
          setPopupState((prev) => ({ ...prev, show: false, box: null }))
        }
        removeBox(box)
        dispatch(setSelectedBoundingBox(undefined))
      }
    },
    [popupState.box, removeBox, dispatch]
  )

  const handleClosePopup = useCallback(() => {
    setPopupState((prev) => ({ ...prev, show: false, box: null }))
  }, [])

  const handleCloseTagSelector = useCallback(() => {
    setPopupState((prev) => ({ ...prev, show: false, box: null }))
    setNewBoundingBox(null) // Clear the new box when tag selector is closed
    setIsDrawing(false) // Ensure drawing state is cleared
    setCreationPhase('idle')
    setCreationAxisStart(null)
  }, [])

  const handleBoxSelection = useCallback(
    (box: AnnotationDataBoundingBox | null) => {
      if (!box) {
        selectedBBoxRef.current = null
        dispatch(setSelectedBoundingBox(undefined))
        setResizingHandle(null)
        return
      }
      // Always allow selecting/re-selecting boxes (even if one is already selected)
      if (box) {
        selectedBBoxRef.current = box
        dispatch(setSelectedBoundingBox(box))
        // Only recenter/zoom if no box was previously selected
        if (!selectedBBox && canvasRef.current && imageRef.current) {
          const bBoxScaleX = canvasRef.current.width / (imageRef.current.naturalWidth || 1)
          const bBoxScaleY = canvasRef.current.height / (imageRef.current.naturalHeight || 1)
          const { zoomLevel: newZoom, panOffset: newPan } = calculateZoomForBBox(
            box,
            canvasRef.current,
            bBoxScaleX,
            bBoxScaleY,
            imageRef.current.naturalWidth,
            imageRef.current.naturalHeight,
            contentWidth, // Pass actual content dimensions
            contentHeight // Pass actual content dimensions
          )
          setZoomLevel(newZoom)
          setPanOffset(newPan)
        }
        // Do not recenter/zoom when switching between boxes while one is already selected
      }
    },
    [dispatch, selectedBBox]
  )

  const handleBoxResize = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const activeBox = selectedBBoxRef.current
      if (!activeBox || !resizingHandle) return

      const { x, y } = getMousePos(e)
      // Convert box from content space to natural space for resizing
      const activeBoxNatural = contentToNatural(activeBox)
      // Ensure consistent base box in natural space
      const migratedBox = migrateBoundingBoxToPoints(
        { ...activeBoxNatural },
        imageRef?.current?.naturalWidth || 1,
        imageRef?.current?.naturalHeight || 1
      )
      const strategy = getGeometryStrategy(migratedBox)
      const resized = strategy.resize(migratedBox, resizingHandle as ResizeHandleName, x, y, {
        // Remove Alt from mouse modifiers for simplicity and to avoid unintended effects
        modifiers: { shift: e.shiftKey, alt: false, ctrl: e.ctrlKey || e.metaKey },
        interaction: {
          pivot: interactionRef.current?.pivot,
          startPoints: interactionRef.current?.startPoints,
        },
      })
      // Convert resized box back to content space before saving
      const resizedContent = naturalToContent(resized)
      const updatedBBox: AnnotationDataBoundingBox = {
        ...resizedContent,
        // Preserve identity and scoring while editing
        class_index: activeBox.class_index,
        confidence: activeBox.confidence,
      }

      // PERFORMANCE FIX: Must update Redux so canvas shows the box moving in real-time
      // PERFORMANCE: Only update local state during drag, NOT Redux!
      // Canvas draws selectedBBox directly (see drawing.ts boxesToDraw)
      // Redux only updates on mouseup - this prevents ALL snippet re-renders during drag
      selectedBBoxRef.current = updatedBBox
      dispatch(setSelectedBoundingBox(updatedBBox))
      // NO dispatch(updateBoundingBox) here - snippets have nothing to do with dragging!
    },
    [resizingHandle, getMousePos, dispatch, getGeometryStrategy, contentToNatural, naturalToContent]
  )

  const getResizeHandle = useCallback(
    (box: AnnotationDataBoundingBox, mouseX: number, mouseY: number): string | null => {
      // Disable handle detection while in any creation phase
      if (creationPhase !== 'idle') return null
      // NOTE: box is expected to already be in natural space (callers convert before calling)
      // Match handle size to box stroke scaling (2 / zoomLevel)
      const strokeWidthCanvas = 2 / zoomLevel
      const handleSizeCanvas = 2 * strokeWidthCanvas
      // Light padding for hit area proportional to stroke
      const hitPaddingCanvas = strokeWidthCanvas
      // Use natural dimensions for coordinate system
      const imgW = imageRef?.current?.naturalWidth || 1
      const imgH = imageRef?.current?.naturalHeight || 1
      const cW = canvasRef?.current?.width || 1
      const cH = canvasRef?.current?.height || 1
      const fitScale = Math.min(cW / imgW, cH / imgH)
      const drawW = imgW * fitScale
      const drawH = imgH * fitScale
      const ctx: InteractionContext = {
        imageWidth: imgW,
        imageHeight: imgH,
        canvasWidth: drawW,
        canvasHeight: drawH,
        zoomLevel,
        handleSizeCanvas,
        hitPaddingCanvas,
      }
      const strategy = getGeometryStrategy(box)
      return strategy.getActiveHandle(box, mouseX, mouseY, ctx)
    },
    [zoomLevel, creationPhase, getGeometryStrategy]
  )

  const boxHasSize = (bBox: AnnotationDataBoundingBox): boolean => {
    return (bBox.xmax ?? 0) - (bBox.xmin ?? 0) !== 0 && (bBox.ymax ?? 0) - (bBox.ymin ?? 0) !== 0
  }

  const handleMouseEvent = useCallback(
    (
      e: React.MouseEvent<HTMLCanvasElement>,
      eventType: 'down' | 'move' | 'up' | 'click' | 'context'
    ) => {
      const { x, y } = getMousePos(e)

      // Handle middle mouse button panning for all project types
      if (eventType === 'down' && e.button === 1) {
        setIsPanning(true)
        lastPanPointRef.current = { x: e.clientX, y: e.clientY }
        return
      }

      // Handle panning movement for all project types
      if (eventType === 'move' && isPanning) {
        const canvas = canvasRef.current
        if (canvas) {
          const rect = canvas.getBoundingClientRect()
          const scaleX = canvas.width / rect.width
          const scaleY = canvas.height / rect.height
          const dxCss = e.clientX - lastPanPointRef.current.x
          const dyCss = e.clientY - lastPanPointRef.current.y
          const dx = (dxCss * scaleX) / zoomLevel
          const dy = (dyCss * scaleY) / zoomLevel
          setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }))
        }
        lastPanPointRef.current = { x: e.clientX, y: e.clientY }
        return
      }

      // IMPORTANT FIX: If we're currently resizing a bounding box, only handle the resize
      // and don't do any other processing for mouse move events
      if (eventType === 'move' && useBBoxes && isEditable && selectedBBox && resizingHandle) {
        e.preventDefault() // Prevent other mouse events
        handleBoxResize(e)
        // After updating, ensure selectedBBox is refreshed from store or local state
        // to avoid stale closures during continuous move
        // No-op: setSelectedBBox is called in handleBoxResize
        return
      }

      // Handle mouse up for all project types
      if (eventType === 'up') {
        setIsPanning(false)
      }

      // For classification projects, only handle left clicks to show tag selector
      if (isClassificationProject) {
        // If a bbox interaction just occurred, suppress popup
        if (suppressTagPopupRef.current) {
          suppressTagPopupRef.current = false
          return
        }
        if (eventType === 'up' && e.button === 0 && popupState.show === false) {
          const rect = canvasRef?.current?.getBoundingClientRect()
          if (rect) {
            let tagX = e.clientX - rect.left
            let tagY = e.clientY - rect.top

            // Adjust position to ensure tag selector is visible
            if (tagX + 250 > rect.width) {
              tagX = tagX - 250
            }

            // Check if mouse is in the bottom half of the viewport
            const isBottomHalf = e.clientY > window.innerHeight / 2

            // If in bottom half, show above mouse, otherwise show below
            if (isBottomHalf) {
              tagY = tagY - 20 // Show above mouse
            } else {
              tagY = tagY + 20 // Show below mouse
            }

            setPopupState((prev) => ({
              ...prev,
              show: true,
              position: { x: tagX, y: tagY },
              box: null,
            }))
          }
        } else if (eventType === 'up' && e.button === 0 && popupState.show === true) {
          setNewBoundingBox(null) // Clear the new box when tag selector is closed
          setIsDrawing(false) // Ensure drawing state is cleared
          setPopupState({ show: false, box: null, position: { x: 0, y: 0 } })
        }
        return
      }

      // Update cursor on hover over handles (skip when Ctrl/Cmd is held to force-create)
      if (eventType === 'move' && useBBoxes && !(e.ctrlKey || e.metaKey)) {
        let cursor = 'default'
        // Only show resize/rotate cursors when the mouse is inside a target box
        let targetBox: AnnotationDataBoundingBox | undefined
        if (selectedBBox) {
          // Convert selected box to natural space for hit testing
          const selectedBBoxNatural = contentToNatural(selectedBBox)
          if (
            x >= (selectedBBoxNatural.xmin ?? 0) &&
            x <= (selectedBBoxNatural.xmax ?? 0) &&
            y >= (selectedBBoxNatural.ymin ?? 0) &&
            y <= (selectedBBoxNatural.ymax ?? 0)
          ) {
            targetBox = selectedBBox
          }
        }
        if (!targetBox) {
          targetBox = visibleBBoxes?.find((b) => {
            // Convert box to natural space for hit testing
            const bNatural = contentToNatural(b)
            const strategy = getGeometryStrategy(bNatural)
            return strategy.hitTest({ x, y }, bNatural)
          })
        }
        if (targetBox) {
          // Convert box to natural space for handle detection
          const targetBoxNatural = contentToNatural(targetBox)
          const handle = getResizeHandle(targetBoxNatural, x, y)
          if (handle) {
            if (handle === 'rotate') {
              const custom = getRotatedResizeCursor(0)
              cursor = custom ? custom.css : 'alias'
            } else {
              cursor = getCursorForHandle(targetBox, handle)
            }
          }
        }
        if (canvasRef.current) canvasRef.current.style.cursor = cursor
      }

      if (useBBoxes && eventType === 'down' && e.button === 0) {
        let handle = undefined
        const isCreating = creationPhase !== 'idle' || isDrawing || !!newBoundingBox
        const forceCreateMode = e.ctrlKey || e.metaKey

        // Check if clicking on a handle of the selected box (priority check - handles can be outside bounds)
        if (selectedBBox && !isCreating && !forceCreateMode && isEditable) {
          // Convert box from content space to natural space for handle detection
          const selectedBBoxNatural = contentToNatural(selectedBBox)
          handle = getResizeHandle(selectedBBoxNatural, x, y)
          if (handle) {
            // Clicking on a handle - start resizing
            suppressTagPopupRef.current = true
            e.preventDefault()
            setResizingHandle(handle)
            // selectedBBoxNatural already converted above for handle detection
            const boxPts =
              selectedBBoxNatural.points && selectedBBoxNatural.points.length === 8
                ? [...selectedBBoxNatural.points]
                : [
                    selectedBBoxNatural.xmin ?? 0,
                    selectedBBoxNatural.ymin ?? 0,
                    selectedBBoxNatural.xmax ?? 0,
                    selectedBBoxNatural.ymin ?? 0,
                    selectedBBoxNatural.xmax ?? 0,
                    selectedBBoxNatural.ymax ?? 0,
                    selectedBBoxNatural.xmin ?? 0,
                    selectedBBoxNatural.ymax ?? 0,
                  ]
            if (handle === 'rotate') {
              // Choose pivot: Ctrl => nearest corner, otherwise center
              const corners = [
                { x: boxPts[0], y: boxPts[1] },
                { x: boxPts[2], y: boxPts[3] },
                { x: boxPts[4], y: boxPts[5] },
                { x: boxPts[6], y: boxPts[7] },
              ]
              let pivot
              if (e.ctrlKey || e.metaKey) {
                let best = corners[0]
                let bestD = Infinity
                for (const c of corners) {
                  const dx = c.x - x
                  const dy = c.y - y
                  const d = dx * dx + dy * dy
                  if (d < bestD) {
                    bestD = d
                    best = c
                  }
                }
                pivot = best
              } else {
                const xs = [boxPts[0], boxPts[2], boxPts[4], boxPts[6]]
                const ys = [boxPts[1], boxPts[3], boxPts[5], boxPts[7]]
                pivot = {
                  x: (Math.min(...xs) + Math.max(...xs)) / 2,
                  y: (Math.min(...ys) + Math.max(...ys)) / 2,
                }
              }
              interactionRef.current = { pivot, startPoints: boxPts, originalPoints: [...boxPts] }
              if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing'
            } else {
              interactionRef.current = { startPoints: boxPts, originalPoints: [...boxPts] }
            }
            return // Early return - we're handling a resize, don't check anything else
          }

          // Not on a handle - check if clicking inside the selected box bounds
          // selectedBBoxNatural already declared above for handle detection
          const strategy = getGeometryStrategy(selectedBBoxNatural)
          const isInBounds = strategy.hitTest({ x, y }, selectedBBoxNatural)
          if (isInBounds) {
            // Clicking inside the box but not on a handle - could be for moving (future feature)
            // For now, just keep the selection
            return
          }
        }

        // If we get here, either no box selected OR clicked outside the selected box
        // Check if clicking on another box
        {
          // if not, get clicked box, if any... (skip when creating)
          const clickedBox =
            !isCreating && !forceCreateMode
              ? visibleBBoxes?.find((box) => {
                  // Convert box from content space to natural space for hit testing
                  const boxNatural = contentToNatural(box)
                  const strategy = getGeometryStrategy(boxNatural)
                  return strategy.hitTest({ x, y }, boxNatural)
                })
              : undefined
          if (clickedBox) {
            suppressTagPopupRef.current = true
            handleBoxSelection(clickedBox)
            // Convert box to natural space for handle detection and interaction
            const clickedBoxNatural = contentToNatural(clickedBox)
            const handle = getResizeHandle(clickedBoxNatural, x, y)
            if (isEditable && handle) {
              e.preventDefault() // Prevent other mouse events
              setResizingHandle(handle)
              const boxPts =
                clickedBoxNatural.points && clickedBoxNatural.points.length === 8
                  ? [...clickedBoxNatural.points]
                  : [
                      clickedBoxNatural.xmin ?? 0,
                      clickedBoxNatural.ymin ?? 0,
                      clickedBoxNatural.xmax ?? 0,
                      clickedBoxNatural.ymin ?? 0,
                      clickedBoxNatural.xmax ?? 0,
                      clickedBoxNatural.ymax ?? 0,
                      clickedBoxNatural.xmin ?? 0,
                      clickedBoxNatural.ymax ?? 0,
                    ]
              if (handle === 'rotate') {
                const corners = [
                  { x: boxPts[0], y: boxPts[1] },
                  { x: boxPts[2], y: boxPts[3] },
                  { x: boxPts[4], y: boxPts[5] },
                  { x: boxPts[6], y: boxPts[7] },
                ]
                let pivot
                if (e.ctrlKey || e.metaKey) {
                  let best = corners[0]
                  let bestD = Infinity
                  for (const c of corners) {
                    const dx = c.x - x
                    const dy = c.y - y
                    const d = dx * dx + dy * dy
                    if (d < bestD) {
                      bestD = d
                      best = c
                    }
                  }
                  pivot = best
                } else {
                  const xs = [boxPts[0], boxPts[2], boxPts[4], boxPts[6]]
                  const ys = [boxPts[1], boxPts[3], boxPts[5], boxPts[7]]
                  pivot = {
                    x: (Math.min(...xs) + Math.max(...xs)) / 2,
                    y: (Math.min(...ys) + Math.max(...ys)) / 2,
                  }
                }
                interactionRef.current = { pivot, startPoints: boxPts, originalPoints: [...boxPts] }
                if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing'
              } else {
                interactionRef.current = { startPoints: boxPts, originalPoints: [...boxPts] }
              }
              return // Early return - we're handling a resize, don't check anything else
            }
            // Box was clicked but no handle - just keep the selection
            return
          }
        }

        // No box clicked - clear selection and start creating new box (if applicable)
        handleBoxSelection(null)
        dispatch(setSelectedBoundingBox(undefined))

        // OBB-only creation state machine; otherwise fallback to axis-aligned drag
        if (!isEditable) {
          return
        }
        if (isOBBProject) {
          if (creationPhase === 'idle') {
            // First click: set axis start
            const p1: Point = { x: Math.round(x), y: Math.round(y) }
            interactionRef.current = {
              ...(interactionRef.current || {}),
              axisStart: p1,
              axisEnd: undefined,
              halfWidthPxImage: undefined,
            }
            setCreationPhase('axisStart')
            setCreationAxisStart(p1)
            setIsDrawing(false)
            setNewBoundingBox(null)
          } else if (creationPhase === 'axisStart' && interactionRef.current?.axisStart) {
            // Second click: set axis end and compute default width from zoom
            const p1 = interactionRef.current.axisStart
            const p2: Point = { x: Math.round(x), y: Math.round(y) }
            if (canvasRef.current && imageRef.current) {
              const desiredScreenWidthPx = 12
              // Use natural dimensions (displayed image) for coordinate system
              const imgW = imageRef.current.naturalWidth
              const imgH = imageRef.current.naturalHeight
              const imageScaleX = imgW / canvasRef.current.width
              const imageScaleY = imgH / canvasRef.current.height
              const imageScale = Math.max(imageScaleX, imageScaleY)
              const defaultHalf = ((desiredScreenWidthPx / 2) * imageScale) / zoomLevel
              const halfWidthPxImage = interactionRef.current?.halfWidthPxImage ?? defaultHalf

              const points = buildObbPoints(p1, p2, halfWidthPxImage)
              const xs = [points[0], points[2], points[4], points[6]]
              const ys = [points[1], points[3], points[5], points[7]]
              const xmin = Math.min(...xs)
              const xmax = Math.max(...xs)
              const ymin = Math.min(...ys)
              const ymax = Math.max(...ys)
              const newBox: AnnotationDataBoundingBox = {
                name: 'newBox',
                points,
                xmin,
                ymin,
                xmax,
                ymax,
                ai_labeled: false,
                confidence: undefined,
                class_index: allBoundingBoxes?.length ?? 0,
              }
              setNewBoundingBox(newBox)
              interactionRef.current = {
                ...(interactionRef.current || {}),
                axisStart: p1,
                axisEnd: p2,
                halfWidthPxImage,
              }
              setCreationPhase('axisFixed')
            }
          } else if (creationPhase === 'axisFixed' && newBoundingBox) {
            // Third click: finalize and open TagSelector
            if (canvasRef.current) {
              const rect = canvasRef.current.getBoundingClientRect()
              let tagX = e.clientX - rect.left
              let tagY = e.clientY - rect.top
              if (tagX + 250 > rect.width) tagX = tagX - 250
              const isBottomHalf = e.clientY > window.innerHeight / 2
              tagY = isBottomHalf ? tagY - 20 : tagY + 20
              if (
                Math.abs(newBoundingBox.xmax ?? 0 - (newBoundingBox.xmin ?? 0)) > 5 &&
                Math.abs(newBoundingBox.ymax ?? 0 - (newBoundingBox.ymin ?? 0)) > 5
              ) {
                setPopupState({ show: true, position: { x: tagX, y: tagY }, box: null })
              } else {
                setPopupState({ show: false, position: { x: tagX, y: tagY }, box: null })
              }
              setIsDrawing(false)
              setDrawingStartPoint(null)
              setCreationPhase('idle')
              setCreationAxisStart(null)
            }
          }
        } else {
          // Not OBB project - use axis-aligned drag creation
          setIsDrawing(true)
          const startPoint: Point = { x: Math.round(x), y: Math.round(y) }
          setDrawingStartPoint(startPoint)
          const endPoint: Point = { x: Math.round(x), y: Math.round(y) }

          // Create initial box without normalization
          const newBox: AnnotationDataBoundingBox = {
            name: 'newBox',
            points: [
              startPoint.x,
              startPoint.y,
              endPoint.x,
              endPoint.y,
              endPoint.x,
              endPoint.y,
              startPoint.x,
              startPoint.y,
            ],
            ai_labeled: false,
            confidence: undefined,
            class_index: allBoundingBoxes?.length ?? 0,
            xmin: startPoint.x,
            ymin: startPoint.y,
            xmax: endPoint.x,
            ymax: endPoint.y,
          }
          setNewBoundingBox(newBox)
        }

        setIsPanning(false) // Don't start panning when drawing
        lastPanPointRef.current = { x: e.clientX, y: e.clientY }
      } else if (eventType === 'move') {
        // During OBB axisStart, if a half-width was set (via wheel), update the preview OBB as mouse moves
        if (
          useBBoxes &&
          isOBBProject &&
          creationPhase === 'axisStart' &&
          interactionRef.current?.axisStart &&
          interactionRef.current?.halfWidthPxImage
        ) {
          const p1 = interactionRef.current.axisStart
          const p2: Point = { x: Math.round(x), y: Math.round(y) }
          const half = interactionRef.current.halfWidthPxImage
          const points = buildObbPoints(p1, p2, half)
          const xs = [points[0], points[2], points[4], points[6]]
          const ys = [points[1], points[3], points[5], points[7]]
          const updatedBox: AnnotationDataBoundingBox = {
            name: 'newBox',
            points,
            xmin: Math.min(...xs),
            ymin: Math.min(...ys),
            xmax: Math.max(...xs),
            ymax: Math.max(...ys),
            ai_labeled: false,
            confidence: undefined,
            class_index: allBoundingBoxes?.length ?? 0,
          }
          setNewBoundingBox(updatedBox)
          return
        }
        if (useBBoxes && !isOBBProject && isDrawing && newBoundingBox && drawingStartPoint) {
          // Use the constant drawing start point, not the normalized xmin/ymin
          const endPoint: Point = { x: Math.round(x), y: Math.round(y) }

          // Create points without normalization during drawing
          const updatedBox: AnnotationDataBoundingBox = {
            ...newBoundingBox,
            points: [
              drawingStartPoint.x,
              drawingStartPoint.y, // top-left
              endPoint.x,
              drawingStartPoint.y, // top-right
              endPoint.x,
              endPoint.y, // bottom-right
              drawingStartPoint.x,
              endPoint.y, // bottom-left
            ],
            xmin: drawingStartPoint.x,
            ymin: drawingStartPoint.y,
            xmax: endPoint.x,
            ymax: endPoint.y,
          }

          setNewBoundingBox(updatedBox)
        }
        // Handle for mouse moves was moved to the top for better control flow
      } else if (eventType === 'up') {
        if (isEditable && selectedBBox && resizingHandle) {
          // PERFORMANCE FIX: Use ref to get the ACTUAL final position after drag
          // selectedBBox from Redux might be stale due to async updates
          const finalBox = selectedBBoxRef.current || selectedBBox

          // Normalize per strategy on mouse up
          const strategy = getGeometryStrategy(finalBox)
          const normalized = strategy.normalizeAfterMouseUp(finalBox)
          const cleanedBBox: AnnotationDataBoundingBox = {
            ...normalized,
            // Preserve identity and scoring
            class_index: finalBox.class_index,
            confidence: finalBox.confidence,
          }

          // NOW update Redux with the final position (for snippets and undo)
          dispatch(updateBoundingBox({ updatedBBox: cleanedBBox }))
          dispatch(setSelectedBoundingBox(cleanedBBox))
          // Mark the box as edited so it shows an indicator
          if (cleanedBBox.class_index !== undefined) {
            dispatch(markBoundingBoxAsEdited(cleanedBBox.class_index))
          }
          setResizingHandle(null)
          if (canvasRef.current) canvasRef.current.style.cursor = 'default'

          // Only push to undo if shape actually changed from originalPoints
          const original = interactionRef.current?.originalPoints
          interactionRef.current = null
          const newPts =
            cleanedBBox.points && cleanedBBox.points.length === 8
              ? cleanedBBox.points
              : [
                  cleanedBBox.xmin ?? 0,
                  cleanedBBox.ymin ?? 0,
                  cleanedBBox.xmax ?? 0,
                  cleanedBBox.ymin ?? 0,
                  cleanedBBox.xmax ?? 0,
                  cleanedBBox.ymax ?? 0,
                  cleanedBBox.xmin ?? 0,
                  cleanedBBox.ymax ?? 0,
                ]
          const changed =
            !original || original.length !== 8 || original.some((v, i) => v !== newPts[i])
          if (changed) {
            dispatch(saveStateToHistory())
          }
        } else if (
          isEditable &&
          !isOBBProject &&
          isDrawing &&
          newBoundingBox &&
          drawingStartPoint
        ) {
          // Create final bounding box after drawing is complete (all in pixels)
          const finalBox: AnnotationDataBoundingBox = {
            ...newBoundingBox,
            points: [
              drawingStartPoint.x, // top-left x
              drawingStartPoint.y, // top-left y
              newBoundingBox.xmax ?? drawingStartPoint.x, // top-right x
              drawingStartPoint.y, // top-right y
              newBoundingBox.xmax ?? drawingStartPoint.x, // bottom-right x
              newBoundingBox.ymax ?? drawingStartPoint.y, // bottom-right y
              drawingStartPoint.x, // bottom-left x
              newBoundingBox.ymax ?? drawingStartPoint.y, // bottom-left y
            ],
            xmin: Math.min(drawingStartPoint.x, newBoundingBox.xmax ?? drawingStartPoint.x),
            ymin: Math.min(drawingStartPoint.y, newBoundingBox.ymax ?? drawingStartPoint.y),
            xmax: Math.max(drawingStartPoint.x, newBoundingBox.xmax ?? drawingStartPoint.x),
            ymax: Math.max(drawingStartPoint.y, newBoundingBox.ymax ?? drawingStartPoint.y),
          }

          setNewBoundingBox(finalBox)

          // Only show tag selector if the box has a minimum size
          if (canvasRef?.current) {
            const rect = canvasRef.current.getBoundingClientRect()

            let tagX = e.clientX - rect.left
            let tagY = e.clientY - rect.top

            // Adjust position to ensure tag selector is visible
            if (tagX + 250 > rect.width) {
              tagX = tagX - 250
            }

            // Check if mouse is in the bottom half of the viewport
            const isBottomHalf = e.clientY > window.innerHeight / 2

            // If in bottom half, show above mouse, otherwise show below
            if (isBottomHalf) {
              tagY = tagY - 20 // Show above mouse
            } else {
              tagY = tagY + 20 // Show below mouse
            }

            if (
              Math.abs(newBoundingBox.xmax ?? 0 - (newBoundingBox.xmin ?? 0)) > 5 &&
              Math.abs(newBoundingBox.ymax ?? 0 - (newBoundingBox.ymin ?? 0)) > 5
            ) {
              setPopupState({
                show: true,
                position: { x: tagX, y: tagY },
                box: null,
              })
            } else if (
              Math.abs(newBoundingBox.xmax ?? 0 - (newBoundingBox.xmin ?? 0)) < 5 ||
              Math.abs(newBoundingBox.ymax ?? 0 - (newBoundingBox.ymin ?? 0)) < 5
            ) {
              setPopupState({
                show: false,
                position: { x: tagX, y: tagY },
                box: null,
              })
            }
            setIsDrawing(false) // Stop drawing when showing tag selector
            setDrawingStartPoint(null) // Clear the drawing start point
          } else {
            setNewBoundingBox(null)
            setIsDrawing(false)
            setDrawingStartPoint(null) // Clear the drawing start point
          }
        }

        setIsPanning(false)

        // Only clear the box if right-clicking or explicitly canceling
        if (e.button === 2) {
          setNewBoundingBox(null) // Clear the new box when tag selector is closed
          setIsDrawing(false) // Ensure drawing state is cleared
          setPopupState((prev) => ({ ...prev, show: false, box: null }))
        }
      } else if (eventType === 'context') {
        e.preventDefault()
        setNewBoundingBox(null) // Clear the new box when tag selector is closed
        setIsDrawing(false) // Ensure drawing state is cleared
        setPopupState((prev) => ({ ...prev, show: false, box: null }))
      }
    },
    [
      getMousePos,
      useBBoxes,
      selectedBBox,
      resizingHandle,
      zoomLevel,
      isPanning,
      isEditable,
      isClassificationProject,
      handleBoxResize,
      popupState.show,
      visibleBBoxes,
      getGeometryStrategy,
      getResizeHandle,
      getCursorForHandle,
      handleBoxSelection,
      dispatch,
      isOBBProject,
      creationPhase,
      newBoundingBox,
      buildObbPoints,
      allBoundingBoxes?.length,
      isDrawing,
      drawingStartPoint,
      contentToNatural,
    ]
  )

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current) return

      // UX: Prevent default scroll behavior for smooth zoom experience
      e.preventDefault()

      // OBB creation width-adjust mode during axisStart: adjust width using cursor as temporary axis end
      if (
        useBBoxes &&
        isOBBProject &&
        creationPhase === 'axisStart' &&
        interactionRef.current?.axisStart &&
        !e.ctrlKey &&
        !e.metaKey &&
        imageRef.current
      ) {
        const rect = canvasRef.current.getBoundingClientRect()
        const scaleX = canvasRef.current.width / rect.width
        const scaleY = canvasRef.current.height / rect.height
        const canvasX = (e.clientX - rect.left) * scaleX
        const canvasY = (e.clientY - rect.top) * scaleY
        // Invert zoom/pan to pre-transform canvas space
        const preX =
          (canvasX - canvasRef.current.width / 2) / zoomLevel +
          canvasRef.current.width / 2 -
          panOffset.x
        const preY =
          (canvasY - canvasRef.current.height / 2) / zoomLevel +
          canvasRef.current.height / 2 -
          panOffset.y
        // Convert to image space using natural dimensions
        const imgW = imageRef.current.naturalWidth
        const imgH = imageRef.current.naturalHeight
        const imageScaleX = imgW / canvasRef.current.width
        const imageScaleY = imgH / canvasRef.current.height
        const imgX = preX * imageScaleX
        const imgY = preY * imageScaleY

        const screenStepPx = 2
        const imageScale = Math.max(imageScaleX, imageScaleY)
        const stepImage = (screenStepPx * imageScale) / zoomLevel
        const minHalfImage = (1 * imageScale) / zoomLevel
        const sign = e.deltaY > 0 ? -1 : 1
        const desiredScreenWidthPx = 12
        const defaultHalf = ((desiredScreenWidthPx / 2) * imageScale) / zoomLevel
        const currentHalf = interactionRef.current.halfWidthPxImage || defaultHalf
        const nextHalf = Math.max(minHalfImage, currentHalf + sign * stepImage)

        const p1 = interactionRef.current.axisStart
        const p2: Point = { x: Math.round(imgX), y: Math.round(imgY) }
        const points = buildObbPoints(p1, p2, nextHalf)
        const xs = [points[0], points[2], points[4], points[6]]
        const ys = [points[1], points[3], points[5], points[7]]
        setNewBoundingBox((prev) => ({
          name: prev?.name || 'newBox',
          points,
          xmin: Math.min(...xs),
          ymin: Math.min(...ys),
          xmax: Math.max(...xs),
          ymax: Math.max(...ys),
          ai_labeled: false,
          confidence: undefined,
          class_index: prev?.class_index ?? allBoxesLength ?? 0,
        }))
        interactionRef.current = {
          ...(interactionRef.current || {}),
          axisStart: p1,
          halfWidthPxImage: nextHalf,
        }
        return
      }

      // OBB creation width-adjust mode: active during axisFixed unless Ctrl/Meta held
      if (
        useBBoxes &&
        isOBBProject &&
        creationPhase === 'axisFixed' &&
        interactionRef.current?.axisStart &&
        interactionRef.current?.axisEnd &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        if (!imageRef.current) return
        const screenStepPx = 2
        // Use natural dimensions (displayed image) for coordinate system
        const imgW = imageRef.current.naturalWidth
        const imgH = imageRef.current.naturalHeight
        const imageScaleX = imgW / canvasRef.current.width
        const imageScaleY = imgH / canvasRef.current.height
        const imageScale = Math.max(imageScaleX, imageScaleY)
        const stepImage = (screenStepPx * imageScale) / zoomLevel
        const minHalfImage = (1 * imageScale) / zoomLevel
        const sign = e.deltaY > 0 ? -1 : 1
        const currentHalf = interactionRef.current.halfWidthPxImage || minHalfImage
        const nextHalf = Math.max(minHalfImage, currentHalf + sign * stepImage)

        const p1 = interactionRef.current.axisStart
        const p2 = interactionRef.current.axisEnd
        const points = buildObbPoints(p1, p2, nextHalf)
        const xs = [points[0], points[2], points[4], points[6]]
        const ys = [points[1], points[3], points[5], points[7]]
        const xmin = Math.min(...xs)
        const xmax = Math.max(...xs)
        const ymin = Math.min(...ys)
        const ymax = Math.max(...ys)
        setNewBoundingBox((prev) => ({
          name: prev?.name || 'newBox',
          points,
          xmin,
          ymin,
          xmax,
          ymax,
          ai_labeled: false,
          confidence: undefined,
          class_index: prev?.class_index ?? allBoxesLength ?? 0,
        }))
        interactionRef.current = {
          ...(interactionRef.current || {}),
          axisStart: p1,
          axisEnd: p2,
          halfWidthPxImage: nextHalf,
        }
        return
      }

      // Default: zoom behavior (also when Ctrl pressed during OBB creation)

      // UX: Lower sensitivity for smoother, more controlled zoom (especially on trackpads)
      const zoomSensitivity = 0.0005
      const oldZoom = zoomLevel
      const newZoomLevel = oldZoom * (1 - e.deltaY * zoomSensitivity)
      const clampedZoom = Math.max(0.5, Math.min(50, newZoomLevel)) // Limit zoom

      // Work entirely in canvas pixel space to avoid CSS scaling issues
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height
      const mouseCanvasX = (e.clientX - rect.left) * scaleX
      const mouseCanvasY = (e.clientY - rect.top) * scaleY

      const centerX = canvas.width / 2
      const centerY = canvas.height / 2

      // Compute world point under the mouse before zoom change (invert current transform)
      const worldX = (mouseCanvasX - centerX) / oldZoom + centerX - panOffset.x
      const worldY = (mouseCanvasY - centerY) / oldZoom + centerY - panOffset.y

      // Compute new pan (in pre-scale space) so this world point remains under the cursor
      // Derived from: screen = clampedZoom * (world - center + pan) + center
      // Solve for pan: pan = (screen - center) / clampedZoom - (world - center)
      const newPanX = (mouseCanvasX - centerX) / clampedZoom - (worldX - centerX)
      const newPanY = (mouseCanvasY - centerY) / clampedZoom - (worldY - centerY)

      // UX: Use flushSync to prevent React 18 batching from causing choppy zoom
      // This forces synchronous render so each wheel event sees the updated zoom value
      flushSync(() => {
        setZoomLevel(clampedZoom)
        setPanOffset({ x: newPanX, y: newPanY })
      })

      // Mouse position update
      if (useBBoxes && imageRef.current) {
        // Update mouse position estimate in image space using contain-fit mapping
        // Use natural dimensions (displayed image) for coordinate system
        const imgW2 = imageRef.current.naturalWidth || 1
        const imgH2 = imageRef.current.naturalHeight || 1
        const fitScale2 = Math.min(canvas.width / imgW2, canvas.height / imgH2)
        const drawW2 = imgW2 * fitScale2
        const drawH2 = imgH2 * fitScale2
        const offsetX2 = (canvas.width - drawW2) / 2
        const offsetY2 = (canvas.height - drawH2) / 2
        const newWorldX = (mouseCanvasX - centerX) / clampedZoom + centerX - newPanX
        const newWorldY = (mouseCanvasY - centerY) / clampedZoom + centerY - newPanY
        const newXImage = (newWorldX - offsetX2) / fitScale2
        const newYImage = (newWorldY - offsetY2) / fitScale2
        setMousePosition({ x: newXImage, y: newYImage })
      }
    },
    [zoomLevel, panOffset, useBBoxes, isOBBProject, creationPhase, allBoxesLength, buildObbPoints]
  )

  const handleTagSelect = useCallback(
    (tag: Tag) => {
      if (isClassificationProject) {
        if (!editingTagIds.includes(tag.id)) {
          dispatch(saveStateToHistory())
          dispatch(addEditingTagId(tag.id))
          const classIndex = Math.max(...allBoundingBoxes.map((box) => box.class_index))
          dispatch(
            addBoundingBox({
              name: tag.id,
              points: [0, 0, 0, 0, 0, 0, 0, 0],
              xmin: 0,
              ymin: 0,
              xmax: 0,
              ymax: 0,
              class_index: classIndex + 1,
              confidence: undefined,
              ai_labeled: false,
            })
          )
        }
        setPopupState((prev) => ({ ...prev, show: false, box: null }))
        return
      }

      setPopupState({
        show: false,
        position: { x: 0, y: 0 },
        box: null,
      })

      if (useBBoxes && newBoundingBox) {
        // Ensure the new bounding box has points (all in pixels)
        // Use natural dimensions (displayed image) for coordinate system
        const imageWidth = imageRef?.current?.naturalWidth || 1
        const imageHeight = imageRef?.current?.naturalHeight || 1

        // Migrate the new bounding box to ensure it has points (in natural space)
        const migratedBox = migrateBoundingBoxToPoints(newBoundingBox, imageWidth, imageHeight)

        // Convert from natural space to content space before saving to Redux
        const migratedBoxContent = naturalToContent(migratedBox)

        const finalBox: AnnotationDataBoundingBox = {
          ...migratedBoxContent,
          name: tag.id,
          class_index: allBoundingBoxes.length,
          ai_labeled: false,
        }

        if (boxHasSize(finalBox)) {
          dispatch(saveStateToHistory())
          dispatch(addBoundingBox(finalBox))
        }
      } else if (useBBoxes && selectedBBox) {
        // selectedBBox is already in content space, no conversion needed
        dispatch(saveStateToHistory())
        dispatch(
          addBoundingBox({
            ...selectedBBox,
            name: tag.id,
            class_index: allBoundingBoxes.length,
          })
        )
      }
      setNewBoundingBox(null)
      setPopupState((prev) => ({ ...prev, show: false, box: null }))
      setIsDrawing(false)
      setDrawingStartPoint(null)
    },
    [
      isClassificationProject,
      useBBoxes,
      newBoundingBox,
      selectedBBox,
      editingTagIds,
      dispatch,
      allBoundingBoxes,
      naturalToContent,
    ]
  )

  // Keyboard interactions: delegate to active strategy
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts when typing in inputs/textareas/contenteditable
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        const isEditableEl =
          target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
        if (isEditableEl) return
      }

      // Global Undo/Redo (ensure availability even if parent listener order changes)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault()
        dispatch(undoAction())
        return
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault()
        dispatch(redoAction())
        return
      }

      // Zoom In/Out with '+' and '-' keys (also support '=' and '_')
      if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        if (e.altKey) {
          // Alt + '+' pans right
          const stepScreenPx = 50
          const dx = stepScreenPx / zoomLevel
          setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y }))
        } else if (e.ctrlKey || e.metaKey) {
          // Ctrl/Cmd + '+' pans up (move view up → increase panOffset.y)
          const stepScreenPx = 50
          const dy = stepScreenPx / zoomLevel
          setPanOffset((prev) => ({ x: prev.x, y: prev.y + dy }))
        } else {
          const factor = 1.1
          const next = Math.max(0.5, Math.min(50, zoomLevel * factor))
          setZoomLevel(next)
        }
        return
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        if (e.altKey) {
          // Alt + '-' pans left
          const stepScreenPx = 50
          const dx = stepScreenPx / zoomLevel
          setPanOffset((prev) => ({ x: prev.x - dx, y: prev.y }))
        } else if (e.ctrlKey || e.metaKey) {
          // Ctrl/Cmd + '-' pans down (move view down → decrease panOffset.y)
          const stepScreenPx = 50
          const dy = stepScreenPx / zoomLevel
          setPanOffset((prev) => ({ x: prev.x, y: prev.y - dy }))
        } else {
          const factor = 1 / 1.1
          const next = Math.max(0.5, Math.min(50, zoomLevel * factor))
          setZoomLevel(next)
        }
        return
      }
      // Cancel OBB creation with Escape
      if (
        e.key === 'Escape' &&
        useBBoxes &&
        isOBBProject &&
        (creationPhase !== 'idle' || newBoundingBox)
      ) {
        e.preventDefault()
        setCreationPhase('idle')
        setCreationAxisStart(null)
        setNewBoundingBox(null)
        setIsDrawing(false)
        interactionRef.current = null
        if (canvasRef.current) canvasRef.current.style.cursor = 'default'
        return
      }
      // Global selection cycling with Tab / Shift+Tab
      if (e.key === 'Tab') {
        if (!useBBoxes) return
        // Use the list from the currently selected tab if provided; otherwise fallback
        const boxes = visibleBBoxes
        if (!boxes || boxes.length === 0) return
        e.preventDefault()
        const current = selectedBBoxRef.current
        const currentIdx = current
          ? boxes.findIndex((b) => b.class_index === current.class_index)
          : -1
        const dir = e.shiftKey ? -1 : 1
        const nextIdx = (((currentIdx + dir) % boxes.length) + boxes.length) % boxes.length
        const nextBox = boxes[nextIdx]
        if (nextBox) {
          dispatch(setSelectedBoundingBox(nextBox))
          selectedBBoxRef.current = nextBox
          // Adjust zoom/pan to fit the selected bbox in view
          if (canvasRef.current && imageRef.current) {
            const bBoxScaleX = canvasRef.current.width / (imageRef.current.naturalWidth || 1)
            const bBoxScaleY = canvasRef.current.height / (imageRef.current.naturalHeight || 1)
            const { zoomLevel: newZoom, panOffset: newPan } = calculateZoomForBBox(
              nextBox,
              canvasRef.current,
              bBoxScaleX,
              bBoxScaleY,
              imageRef.current.naturalWidth,
              imageRef.current.naturalHeight,
              contentWidth, // Pass actual content dimensions
              contentHeight // Pass actual content dimensions
            )
            setZoomLevel(newZoom)
            setPanOffset(newPan)
          }
        }
        return
      }

      // Delete current selection with Delete (only when editable)
      if (e.key === 'Delete') {
        const activeBox = selectedBBoxRef.current
        if (!useBBoxes || !isEditable || !activeBox) return
        e.preventDefault()
        handleBoxDelete(activeBox)
        return
      }

      const activeBox = selectedBBoxRef.current
      if (!useBBoxes || !isEditable || !activeBox) return
      const strategy = getGeometryStrategy(activeBox)
      if (!strategy.onKeyDown) return
      const modifiers: KeyModifiers = {
        shift: e.shiftKey,
        ctrl: e.ctrlKey || e.metaKey,
        alt: e.altKey,
      }
      const updated = strategy.onKeyDown(activeBox, e.key, modifiers, {
        // Use natural dimensions (displayed image) for coordinate system
        imageWidth: imageRef?.current?.naturalWidth || 1,
        imageHeight: imageRef?.current?.naturalHeight || 1,
        canvasWidth: canvasRef?.current?.width || 1,
        canvasHeight: canvasRef?.current?.height || 1,
        zoomLevel,
        handleSizeCanvas: Math.min(18, 18 / zoomLevel),
        hitPaddingCanvas: Math.max(6, 12 / zoomLevel),
      })
      if (updated) {
        e.preventDefault()
        const merged: AnnotationDataBoundingBox = {
          ...updated,
          // Preserve identity and scoring during keyboard edits
          class_index: activeBox.class_index,
          confidence: activeBox.confidence,
        }
        dispatch(updateBoundingBox({ updatedBBox: merged }))
        selectedBBoxRef.current = merged
        dispatch(setSelectedBoundingBox(merged))
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    useBBoxes,
    getGeometryStrategy,
    zoomLevel,
    dispatch,
    visibleBBoxes,
    handleBoxDelete,
    isOBBProject,
    creationPhase,
    newBoundingBox,
    isEditable,
  ])

  return {
    canvasRef,
    imageRef,
    handleMouseEvent,
    handleWheel,
    zoomLevel,
    setZoomLevel,
    panOffset,
    setPanOffset,
    newBoundingBox,
    selectedBBox,
    handleBoxSelection,
    setNewBoundingBox,
    handleTagSelect,
    mousePosition,
    recenterImage,
    undo,
    redo,
    reset,
    removeAll,
    removeAllTags,
    contrast,
    setContrast,
    handleDoubleClick,
    popupState,
    setPopupState,
    handleTagChange,
    handleBoxDelete,
    removeBox,
    handleClosePopup,
    handleCloseTagSelector,
    resizingHandle,
    creationAxisStart,
    creationPhase,
  }
}
